import { useRef, useState, useEffect } from 'react';

// VAD Threshold - This value might need tuning based on environment and microphone sensitivity.
// A higher value means more silence will be filtered out.
const VAD_THRESHOLD = 0.005; // Example: 0.005 for quiet environments, 0.01-0.03 for noisier ones.

export default function useVoiceBotCall(
  socketUrl,
  startAnimation,
  stopAnimation,
  setIsPrimarySpeaking
) {
  // State variables for UI feedback
  const [isMuted, setIsMuted] = useState(false);
  const [isCalling, setIsCalling] = useState(false);
  const [isCallConnected, setIsCallConnected] = useState(false);

  // Refs to hold mutable objects that don't trigger re-renders
  const socketRef = useRef(null); // WebSocket connection
  const audioCtxRef = useRef(null); // Web Audio API AudioContext
  const micStreamRef = useRef(null); // Microphone MediaStream
  const scriptNodeRef = useRef(null); // ScriptProcessorNode for mic processing (deprecated, but used for direct control)
  const isMutedRef = useRef(isMuted);

  // Playback queue for incoming audio from the bot
  const playbackQueue = useRef([]); // Stores decoded Float32Array audio chunks
  const isPlayingAudio = useRef(false); // Flag to prevent multiple playback loops
  const currentPlaybackSource = useRef(null); // Reference to the currently playing AudioBufferSourceNode

  // New ref to track if the first audio chunk has been sent
  const isFirstAudioChunkSentRef = useRef(false);

  /**
   * Calculates the Root Mean Square (RMS) of an audio buffer.
   * This is a simple measure of the average power of the audio signal,
   * used here for basic Voice Activity Detection (VAD).
   * @param {Float32Array} buffer - The audio data buffer (e.g., from microphone input).
   * @returns {number} - The RMS value, a non-negative number.
   */
  const calculateRMS = (buffer) => {
    let sum = 0;
    for (let i = 0; i < buffer.length; i++) {
      sum += buffer[i] * buffer[i]; // Sum of squares
    }
    return Math.sqrt(sum / buffer.length); // Square root of the mean of squares
  };

  /**
   * Decodes base64 encoded PCM16 audio data into a Float32Array.
   * This is necessary for Web Audio API processing.
   * @param {string} base64 - Base64 encoded PCM16 audio string.
   * @returns {Float32Array} - Decoded audio samples as Float32Array.
   */
  const decodePCM16 = (base64) => {
    const binary = atob(base64); // Decode base64 to binary string
    const len = binary.length / 2; // Each sample is 2 bytes (16-bit)
    const floatBuffer = new Float32Array(len); // Create a Float32Array for Web Audio API

    for (let i = 0; i < len; i++) {
      // Read 16-bit little-endian value
      const lo = binary.charCodeAt(i * 2);
      const hi = binary.charCodeAt(i * 2 + 1);
      let value = (hi << 8) | lo; // Combine bytes to form 16-bit integer

      // Handle signed 16-bit integer (two's complement)
      if (value & 0x8000) {
        // If the most significant bit is set, it's a negative number
        value = value - 0x10000; // Convert to negative value
      }
      // Normalize to -1.0 to 1.0 range for Float32Array
      floatBuffer[i] = value / 32768.0; // 32768 is 2^15, max value for signed 16-bit is 32767
    }
    return floatBuffer;
  };

  /**
   * Schedules and plays the next audio chunk(s) from the playback queue.
   * This function ensures sequential playback by chaining 'onended' events.
   */
  const scheduleNextPlayback = () => {
    // If no audio context or queue is empty, stop playback and reset flags
    if (!audioCtxRef.current || playbackQueue.current.length === 0) {
      isPlayingAudio.current = false;
      stopAnimation();
      currentPlaybackSource.current = null;
      return;
    }

    // Concatenate all available chunks in the queue into a single buffer
    // This reduces the overhead of creating many small AudioBufferSourceNodes
    const totalLength = playbackQueue.current.reduce(
      (acc, chunk) => acc + chunk.length,
      0
    );
    const combinedBuffer = new Float32Array(totalLength);
    let offset = 0;
    while (playbackQueue.current.length > 0) {
      const chunk = playbackQueue.current.shift(); // Take chunks from the front of the queue
      combinedBuffer.set(chunk, offset); // Copy chunk data to the combined buffer
      offset += chunk.length;
    }

    // Create an AudioBuffer from the combined audio data
    const audioBuffer = audioCtxRef.current.createBuffer(
      1, // mono channel
      combinedBuffer.length, // total number of samples
      audioCtxRef.current.sampleRate // Use the AudioContext's sample rate (24000 Hz)
    );
    audioBuffer.getChannelData(0).set(combinedBuffer); // Set the audio data to the buffer

    // Create an AudioBufferSourceNode to play the audioBuffer
    const source = audioCtxRef.current.createBufferSource();
    source.buffer = audioBuffer;
    source.connect(audioCtxRef.current.destination); // Connect to speakers

    // When this source finishes playing, schedule the next one
    source.onended = () => {
      scheduleNextPlayback(); // Recursively call to play the next available audio
    };

    source.start(0); // Start playing immediately (at time 0 of the audio context)
    currentPlaybackSource.current = source; // Store reference to the current source
    isPlayingAudio.current = true; // Set flag to indicate audio is playing
    startAnimation();
  };

  /**
   * Handles incoming 'audio_reply' messages from the WebSocket.
   * Decodes the audio and adds it to the playback queue.
   * @param {object} data - The parsed JSON data from the WebSocket message.
   */
  const handleAudioReply = (data) => {
    const floatBuffer = decodePCM16(data.audio_b64); // Decode the base64 audio
    playbackQueue.current.push(floatBuffer); // Add to the queue

    // If audio is not currently playing, start the playback chain
    // Otherwise, the new chunk will be picked up by the ongoing playback chain
    if (!isPlayingAudio.current) {
      scheduleNextPlayback();
    }
  };

  /**
   * Handles an 'interrupt' event from the server.
   * Clears the playback queue and stops any currently playing audio.
   */
  const handleInterrupt = () => {
    console.log('Received interrupt event. Clearing audio buffer.');
    playbackQueue.current = []; // Clear the queue

    if (currentPlaybackSource.current) {
      currentPlaybackSource.current.stop(); // Stop current playback
      currentPlaybackSource.current.disconnect(); // Disconnect to release resources
      currentPlaybackSource.current = null;
    }
    isPlayingAudio.current = false; // Reset playback flag
    stopAnimation();
  };

  /**
   * General WebSocket message handler.
   * Parses incoming messages and dispatches them to appropriate handlers.
   * @param {MessageEvent} event - The WebSocket message event.
   */
  const handleSocketMessage = (event) => {
    try {
      const parsed = JSON.parse(event.data);
      if (
        parsed.event_type === 'audio_reply' &&
        parsed.audio_format === 'pcm'
      ) {
        handleAudioReply(parsed);
      } else if (parsed.event_type === 'call_status') {
        // Optional: Log or handle call status messages from the bot
        console.log('Call status:', parsed.status);
      } else if (parsed.event_type === 'interrupt') {
        handleInterrupt();
      }
    } catch (e) {
      console.error('Failed to parse socket message:', e);
    }
  };

  /**
   * Initiates the voice call: sets up AudioContext, WebSocket, and microphone input.
   */
  const startCall = async () => {
    if (isCalling) return; // Prevent starting multiple calls
    setIsCalling(true);
    isFirstAudioChunkSentRef.current = false; // Reset for a new call

    try {
      // Initialize AudioContext with the desired sample rate (24000 Hz)
      audioCtxRef.current = new (window.AudioContext ||
        window.webkitAudioContext)({ sampleRate: 24000 });

      // Initialize WebSocket connection
      socketRef.current = new WebSocket(socketUrl);
      // socketRef.current.binaryType is not set as we send JSON string

      // Set up WebSocket event listeners
      socketRef.current.onmessage = handleSocketMessage;
      socketRef.current.onopen = () => {
        console.log('WebSocket connected.');
        setIsCallConnected(true);
        // Optionally send an initial message to the bot to signal call start
        // This is separate from the first audio chunk 'start' event
        // socketRef.current.send(JSON.stringify({ event_type: 'call_initiated' }));
      };
      socketRef.current.onclose = () => {
        console.log('WebSocket disconnected.');
        stopCall(); // Clean up resources if connection closes unexpectedly
      };
      socketRef.current.onerror = (error) => {
        console.error('WebSocket error:', error);
        stopCall(); // Clean up on WebSocket error
      };

      // Request microphone access from the user
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      micStreamRef.current = stream;

      // Create audio nodes for processing microphone input
      const source = audioCtxRef.current.createMediaStreamSource(stream);
      // ScriptProcessorNode is deprecated, but provides direct audio buffer access.
      // For new projects, consider AudioWorkletNode for more robust processing.
      const scriptNode = audioCtxRef.current.createScriptProcessor(4096, 1, 1); // Buffer size, input channels, output channels

      // This event handler is called when the audio buffer is ready for processing
      scriptNode.onaudioprocess = (e) => {
        // Only send data if WebSocket is open and not muted
        if (
          !socketRef.current ||
          socketRef.current.readyState !== WebSocket.OPEN ||
          isMutedRef.current
        ) {
          return;
        }

        // Get audio data from the microphone input buffer
        const input = e.inputBuffer.getChannelData(0);

        // Calculate RMS for Voice Activity Detection (VAD)
        const rms = calculateRMS(input);

        // Only send audio data if RMS is above the threshold (voice activity detected)
        if (rms > VAD_THRESHOLD) {
          setIsPrimarySpeaking(true);
        } else {
          setIsPrimarySpeaking(false);
          // console.log('Silence detected, not sending (RMS:', rms.toFixed(4), ')'); // Uncomment for debugging VAD
        }
        let currentEventType;
        if (!isFirstAudioChunkSentRef.current) {
          currentEventType = 'start';
          isFirstAudioChunkSentRef.current = true; // Mark as sent
        } else {
          currentEventType = 'audio_chunk';
        }

        // Convert Float32Array (Web Audio API format) to PCM16 (Int16Array)
        const pcmDataLength = input.length * 2; // 2 bytes per sample for 16-bit
        const pcmBuffer = new ArrayBuffer(pcmDataLength);
        const pcmView = new DataView(pcmBuffer);

        for (let i = 0; i < input.length; i++) {
          // Clamp the float value to -1.0 to 1.0 and scale to 16-bit integer range
          let s = Math.max(-1, Math.min(1, input[i]));
          pcmView.setInt16(i * 2, s * 0x7fff, true); // 0x7fff = 32767 (max signed 16-bit value), true for little-endian
        }

        // Convert ArrayBuffer to a string of binary data, then Base64 encode it
        const binaryString = String.fromCharCode.apply(
          null,
          new Uint8Array(pcmBuffer)
        );
        const base64Audio = btoa(binaryString);

        // Create the JSON payload with the requested keys and dynamic event_type
        const payload = {
          event_type: currentEventType,
          audio_format: 'pcm',
          audio_b64: base64Audio,
        };

        // Send the JSON payload as a string over the WebSocket
        socketRef.current.send(JSON.stringify(payload));
      };

      // Connect the audio nodes: Microphone -> ScriptProcessorNode -> AudioContext destination (speakers)
      // Connecting scriptNode to destination allows you to hear your own mic input, which can be useful for debugging.
      source.connect(scriptNode);
      scriptNode.connect(audioCtxRef.current.destination);
      scriptNodeRef.current = scriptNode;
    } catch (error) {
      console.error('Error starting call:', error);
      setIsCalling(false); // Reset calling state on error
      // In a real application, you might want to show a user-friendly error message here
    }
  };

  /**
   * Stops the voice call and cleans up all associated resources.
   */
  const stopCall = () => {
    if (!isCalling) return;

    setIsCalling(false);
    setIsCallConnected(false);
    isPlayingAudio.current = false; // Reset playback flag
    stopAnimation();
    isFirstAudioChunkSentRef.current = false; // Reset for next call

    // Stop and disconnect the currently playing audio source from the bot
    if (currentPlaybackSource.current) {
      currentPlaybackSource.current.stop();
      currentPlaybackSource.current.disconnect();
      currentPlaybackSource.current = null;
    }

    // Close the WebSocket connection
    if (socketRef.current) {
      socketRef.current.close();
      socketRef.current = null;
    }

    // Stop all tracks in the microphone stream
    if (micStreamRef.current) {
      micStreamRef.current.getTracks().forEach((track) => track.stop());
      micStreamRef.current = null;
    }

    // Disconnect the ScriptProcessorNode
    if (scriptNodeRef.current) {
      scriptNodeRef.current.disconnect();
      scriptNodeRef.current = null;
    }

    // Close the AudioContext
    if (audioCtxRef.current) {
      audioCtxRef.current.close().then(() => {
        audioCtxRef.current = null;
      });
    }

    // Clear any remaining audio in the playback queue
    playbackQueue.current = [];
  };

  /**
   * Toggles the microphone mute state.
   * Also enables/disables the microphone track directly.
   */
  const toggleMute = () => {
    setIsMuted((prev) => {
      const newState = !prev;
      // If mic stream exists, enable/disable its audio tracks based on the new mute state
      if (micStreamRef.current) {
        micStreamRef.current.getAudioTracks().forEach((track) => {
          track.enabled = !newState;
        }); // true if unmuted, false if muted
      }
      return newState;
    });
  };

  useEffect(() => {
    return () => {
      stopCall();
    };
  }, []);

  useEffect(() => {
    isMutedRef.current = isMuted;
  }, [isMuted]);

  return {
    isMuted,
    isCalling,
    isCallConnected,
    startCall,
    stopCall,
    toggleMute,
  };
}
